import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Sparkles,
  Trophy,
  Star,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info,
} from "lucide-react";

// Toast variant types following DaisyUI theme
type ToastVariant =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "xp"
  | "levelup"
  | "welcome";

// Toast position options
type ToastPosition =
  | "top-start"
  | "top-center"
  | "top-end"
  | "bottom-start"
  | "bottom-center"
  | "bottom-end";

// Animation configuration constants
const ANIMATION_DURATION = 0.3;
const TOAST_AUTO_DISMISS_DELAY = 5000;

// Toast notification interface
export interface ToastNotification {
  id: string;
  variant: ToastVariant;
  title: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  icon?: React.ReactNode;
  // XP-specific fields
  xpEarned?: number;
  levelUp?: boolean;
  oldLevel?: number;
  newLevel?: number;
}

interface ToastProps {
  notification: ToastNotification;
  position?: ToastPosition;
  onDismiss: (id: string) => void;
}

// Get appropriate icon for toast variant
const getVariantIcon = (
  variant: ToastVariant,
  customIcon?: React.ReactNode,
) => {
  if (customIcon) return customIcon;

  const iconClass = "h-5 w-5 flex-shrink-0";

  switch (variant) {
    case "success":
      return <CheckCircle className={iconClass} />;
    case "error":
      return <XCircle className={iconClass} />;
    case "warning":
      return <AlertCircle className={iconClass} />;
    case "info":
      return <Info className={iconClass} />;
    case "welcome":
      return <Sparkles className={iconClass} />;
    case "levelup":
      return <Trophy className={iconClass} />;
    case "xp":
      return <Star className={iconClass} />;
    default:
      return <Info className={iconClass} />;
  }
};

// Get DaisyUI alert classes for variant
const getVariantClasses = (variant: ToastVariant) => {
  switch (variant) {
    case "success":
      return "alert-success";
    case "error":
      return "alert-error";
    case "warning":
      return "alert-warning";
    case "info":
      return "alert-info";
    case "welcome":
      return "bg-gradient-to-r from-primary to-primary/70 text-primary-content";
    case "levelup":
      return "bg-gradient-to-r from-accent to-accent/70 text-accent-content";
    case "xp":
      return "alert-info bg-blue-500 text-white border-blue-600";
    default:
      return "alert-info";
  }
};

// Animation variants for toast
const toastVariants = {
  initial: {
    opacity: 0,
    y: -50,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    x: 300,
    scale: 0.95,
    transition: {
      duration: ANIMATION_DURATION,
      ease: "easeInOut",
    },
  },
};

const Toast: React.FC<ToastProps> = ({ notification, onDismiss }) => {
  const {
    id,
    variant,
    title,
    message,
    dismissible = true,
    icon,
    xpEarned,
    levelUp,
    oldLevel,
    newLevel,
  } = notification;

  // Auto-dismiss timer
  React.useEffect(() => {
    const duration = notification.duration || TOAST_AUTO_DISMISS_DELAY;
    const timer = setTimeout(() => {
      onDismiss(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, notification.duration, onDismiss]);

  const handleDismiss = () => {
    if (dismissible) {
      onDismiss(id);
    }
  };

  return (
    <motion.div
      variants={toastVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{
        duration: ANIMATION_DURATION,
        ease: "easeOut",
      }}
      className={`alert max-w-md min-w-80 cursor-pointer shadow-lg ${getVariantClasses(variant)}`}
      onClick={handleDismiss}
      role="alert"
      aria-live="polite"
    >
      <div className="flex w-full items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0">{getVariantIcon(variant, icon)}</div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{title}</div>
          <div className="mt-1 text-sm opacity-90">{message}</div>

          {/* XP-specific content */}
          {xpEarned && (
            <div className="mt-1 text-xs opacity-80">+{xpEarned} XP</div>
          )}

          {levelUp && oldLevel && newLevel && (
            <div className="mt-1 text-xs opacity-80">
              Level {oldLevel} → {newLevel}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
            className="btn btn-circle flex-shrink-0 opacity-70 btn-ghost btn-sm hover:opacity-100"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Toast container component to handle positioning
interface ToastContainerProps {
  notifications: ToastNotification[];
  position?: ToastPosition;
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({
  notifications,
  position = "top-end",
  onDismiss,
}) => {
  // Get position classes for DaisyUI toast
  const getPositionClasses = (pos: ToastPosition) => {
    const baseClass = "toast";
    switch (pos) {
      case "top-start":
        return `${baseClass} toast-top toast-start`;
      case "top-center":
        return `${baseClass} toast-top toast-center`;
      case "top-end":
        return `${baseClass} toast-top toast-end`;
      case "bottom-start":
        return `${baseClass} toast-bottom toast-start`;
      case "bottom-center":
        return `${baseClass} toast-bottom toast-center`;
      case "bottom-end":
        return `${baseClass} toast-bottom toast-end`;
      default:
        return `${baseClass} toast-top toast-end`;
    }
  };

  return (
    <div className={getPositionClasses(position)}>
      <AnimatePresence mode="popLayout">
        {notifications.map((notification) => (
          <Toast
            key={notification.id}
            notification={notification}
            position={position}
            onDismiss={onDismiss}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Toast;
